import {
  encodeAbiParameters,
  getAddress,
  isAddress,
  keccak256,
  stringToHex,
} from "viem";
import type { Address, Hex, MiningChallenge, MiningChallengeWire } from "./types";

export const POW_PROTOCOL_VERSION = 1 as const;
export const POW_ALGORITHM = "KECCAK256_ABI_V1" as const;
export const POW_DOMAIN = keccak256(stringToHex("PONS_POW_NFT_V1")) as Hex;
export const MAX_UINT256 = (1n << 256n) - 1n;

const BYTES32 = /^0x[0-9a-fA-F]{64}$/;
const UINT_DECIMAL = /^(0|[1-9][0-9]*)$/;

export function parseUint256(value: string, label = "uint256"): bigint {
  if (value.length > 78 || !UINT_DECIMAL.test(value)) {
    throw new Error(`${label} must be a canonical unsigned decimal string`);
  }
  const parsed = BigInt(value);
  if (parsed < 0n || parsed > MAX_UINT256) throw new Error(`${label} outside uint256 range`);
  return parsed;
}

export function canonicalUint(value: number | string, label: string): string {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${label} must be a safe unsigned integer`);
    return String(value);
  }
  parseUint256(value, label);
  return value;
}

export function requireBytes32(value: string, label: string): Hex {
  if (!BYTES32.test(value)) throw new Error(`${label} must be bytes32 hex`);
  return value.toLowerCase() as Hex;
}

export function normalizeChallenge(input: MiningChallengeWire): MiningChallenge {
  if (input.protocolVersion !== POW_PROTOCOL_VERSION) throw new Error("UNSUPPORTED_POW_PROTOCOL");
  if (input.algorithm !== POW_ALGORITHM) throw new Error("UNSUPPORTED_POW_ALGORITHM");
  if (!isAddress(input.tokenAddress) || !isAddress(input.walletAddress)) throw new Error("INVALID_CHALLENGE_ADDRESS");
  const normalized: MiningChallenge = {
    algorithm: POW_ALGORITHM,
    protocolVersion: POW_PROTOCOL_VERSION,
    instanceId: requireBytes32(input.instanceId, "instanceId"),
    chainId: canonicalUint(input.chainId, "chainId"),
    tokenAddress: getAddress(input.tokenAddress) as Address,
    walletAddress: getAddress(input.walletAddress) as Address,
    challengeId: requireBytes32(input.challengeId, "challengeId"),
    seed: requireBytes32(input.seed, "seed"),
    difficultyEpochId: canonicalUint(input.difficultyEpochId, "difficultyEpochId"),
    targetExclusive: canonicalUint(input.targetExclusive, "targetExclusive"),
    issuedAt: canonicalUint(input.issuedAt, "issuedAt"),
    expiresAt: canonicalUint(input.expiresAt, "expiresAt"),
    challengeCommitment: requireBytes32(input.challengeCommitment, "challengeCommitment"),
    status: input.status,
  };
  const target = parseUint256(normalized.targetExclusive, "targetExclusive");
  if (target < 1n) throw new Error("targetExclusive outside uint256 range");
  if (BigInt(normalized.expiresAt) <= BigInt(normalized.issuedAt)) throw new Error("INVALID_CHALLENGE_DEADLINE");
  const commitment = computeChallengeCommitment(normalized);
  if (commitment.toLowerCase() !== normalized.challengeCommitment.toLowerCase()) {
    throw new Error("CHALLENGE_COMMITMENT_MISMATCH");
  }
  return normalized;
}

export function computeChallengeCommitment(challenge: Omit<MiningChallenge, "challengeCommitment" | "status" | "algorithm" | "protocolVersion">): Hex {
  const target = parseUint256(challenge.targetExclusive, "targetExclusive");
  if (target < 1n) throw new Error("targetExclusive outside uint256 range");
  return keccak256(encodeAbiParameters([
    { type: "bytes32" },
    { type: "bytes32" },
    { type: "uint256" },
    { type: "address" },
    { type: "address" },
    { type: "bytes32" },
    { type: "bytes32" },
    { type: "uint256" },
    { type: "uint256" },
    { type: "uint256" },
    { type: "uint256" },
  ], [
    POW_DOMAIN,
    challenge.instanceId,
    parseUint256(challenge.chainId, "chainId"),
    challenge.tokenAddress,
    challenge.walletAddress,
    challenge.challengeId,
    challenge.seed,
    parseUint256(challenge.difficultyEpochId, "difficultyEpochId"),
    target,
    parseUint256(challenge.issuedAt, "issuedAt"),
    parseUint256(challenge.expiresAt, "expiresAt"),
  ])) as Hex;
}

export function encodePowPayload(commitment: Hex, nonce: bigint): Hex {
  requireBytes32(commitment, "challengeCommitment");
  if (nonce < 0n || nonce > MAX_UINT256) throw new Error("nonce outside uint256 range");
  return encodeAbiParameters([{ type: "bytes32" }, { type: "uint256" }], [commitment, nonce]) as Hex;
}

/** Independent, browser-safe verification used before every server submit. */
export function computePowDigest(commitment: Hex, nonce: bigint): Hex {
  return keccak256(encodePowPayload(commitment, nonce)) as Hex;
}

export function verifyPowCandidate(commitment: Hex, nonceText: string, targetText: string, claimedDigest?: Hex): Hex {
  const nonce = parseUint256(nonceText, "nonce");
  const target = parseUint256(targetText, "targetExclusive");
  if (target < 1n) throw new Error("targetExclusive outside uint256 range");
  const digest = computePowDigest(commitment, nonce);
  if (claimedDigest && digest.toLowerCase() !== claimedDigest.toLowerCase()) throw new Error("CANDIDATE_DIGEST_MISMATCH");
  if (BigInt(digest) >= target) throw new Error("CANDIDATE_ABOVE_TARGET");
  return digest;
}

export function challengeCheckpointKey(challenge: MiningChallenge): string {
  return ["pons-pow-v1", challenge.instanceId, challenge.chainId, challenge.walletAddress.toLowerCase(),
    challenge.tokenAddress.toLowerCase(), challenge.challengeId, challenge.challengeCommitment].join(":");
}

export function hexToBytes32(value: Hex): Uint8Array {
  requireBytes32(value, "bytes32");
  const bytes = new Uint8Array(32);
  for (let index = 0; index < 32; index++) bytes[index] = Number.parseInt(value.slice(2 + index * 2, 4 + index * 2), 16);
  return bytes;
}

export function bytesToHex(bytes: Uint8Array): Hex {
  let output = "0x";
  for (const byte of bytes) output += byte.toString(16).padStart(2, "0");
  return output as Hex;
}

export function uint256ToBytes(value: bigint): Uint8Array {
  if (value < 0n || value > MAX_UINT256) throw new Error("uint256 outside range");
  const bytes = new Uint8Array(32);
  let remaining = value;
  for (let index = 31; index >= 0; index--) {
    bytes[index] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }
  return bytes;
}

export function powPayloadBytes(commitment: Hex, nonce: bigint): Uint8Array {
  const output = new Uint8Array(64);
  output.set(hexToBytes32(commitment), 0);
  output.set(uint256ToBytes(nonce), 32);
  return output;
}

export function digestBytesBelowTarget(digest: Uint8Array, target: Uint8Array): boolean {
  if (digest.length !== 32 || target.length !== 32) throw new Error("digest and target must be 32 bytes");
  for (let index = 0; index < 32; index++) {
    if (digest[index]! < target[index]!) return true;
    if (digest[index]! > target[index]!) return false;
  }
  return false;
}
